import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { addPlayerToClub as addPlayerToClubApi } from "../../api/myClub/addPlayerToClub";
import { deleteSpecificUserPlayers } from "../../api/myClub/deleteSpecificUserPlayers";
import { getUserPlayers } from "../../api/myClub/getUserPlayers";
import { fetchClubStats } from "./myClubSlice";
const initialState = {
    players: [],
    totalAmountOfPlayerPages: 0,
    showFilters: false,
    status: "idle",
    filters: {
        gold: false,
        silver: false,
        bronze: false,
        special: false,
        untradeables: false,
        tradeables: false,
        transferList: false,
        sbcStorage: false,
        duplicates: false,
        manuallyAdded: false,
    },
    manuallyAddPlayerProperties: {
        untradeable: false,
        transferList: false,
        activeSquad: false,
        sbcStorage: false,
    },
    isSelecting: false,
    selectedPlayers: [],
    needsRefresh: false,
};
export const fetchMyClubPlayers = createAsyncThunk("my-club/fetch-players", async ({ page, sortBy, sortDirection, }, { getState }) => {
    const state = getState();
    const activeFilters = getActiveFilters(state.myPlayersReducer.filters);
    const res = await getUserPlayers({
        page,
        filters: activeFilters,
        limit: 20,
        sort: sortBy,
        ascending: sortDirection === "asc",
    });
    return { players: res.players, totalAmountOfPages: res.totalPages };
});
/** Adds a searched card to the club, marked as the add sheet says. */
export const addPlayerToClub = createAsyncThunk("my-club/add-player-to-club", async (player, { getState, dispatch }) => {
    const { manuallyAddPlayerProperties: marks } = getState()
        .myPlayersReducer;
    const res = await addPlayerToClubApi({ ...player, ...marks });
    dispatch(fetchClubStats());
    return res;
});
/** Removes the selected cards from the club (quietly; see the API). */
export const removeSelectedPlayersFromClub = createAsyncThunk("my-club/remove-players-from-club", async (_, { getState, dispatch }) => {
    const { selectedPlayers } = getState().myPlayersReducer;
    await deleteSpecificUserPlayers(selectedPlayers
        .map((p) => p.eaPlayerId)
        .filter((id) => id != null));
    dispatch(fetchClubStats());
    return selectedPlayers;
});
const myPlayersSlice = createSlice({
    name: "my-players",
    initialState,
    reducers: {
        updateShowFilters(state, action) {
            state.showFilters = action.payload;
        },
        updateFilters(state, action) {
            const filter = action.payload;
            if (filter === "tradeables" && state.filters.tradeables) {
                state.filters.untradeables = false;
            }
            else if (filter === "untradeables" && state.filters.untradeables) {
                state.filters.tradeables = false;
            }
            state.filters[filter] = !state.filters[filter];
        },
        updateManuallyAddPlayerProperties(state, action) {
            state.manuallyAddPlayerProperties = action.payload;
        },
        updateIsSelecting(state, action) {
            state.isSelecting = action.payload;
            if (!action.payload) {
                state.selectedPlayers = [];
            }
        },
        selectPlayer(state, action) {
            state.selectedPlayers = [...state.selectedPlayers, action.payload];
        },
        deselectPlayer(state, action) {
            state.selectedPlayers = state.selectedPlayers.filter((player) => player.eaPlayerId !== action.payload.eaPlayerId);
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchMyClubPlayers.fulfilled, (state, action) => {
            state.players = action.payload.players;
            state.totalAmountOfPlayerPages = action.payload.totalAmountOfPages;
            state.status = "fulfilled";
        })
            .addCase(fetchMyClubPlayers.pending, (state) => {
            state.status = "pending";
            state.needsRefresh = false;
        })
            // Refetched rather than inserted: a search result lacks the club fields
            // a row shows (profit, version), and the sort may not put it first.
            .addCase(addPlayerToClub.fulfilled, (state) => {
            state.needsRefresh = true;
        })
            .addCase(removeSelectedPlayersFromClub.fulfilled, (state, action) => {
            const removed = new Set(action.payload.map((p) => p.eaPlayerId));
            state.players = state.players.filter((p) => !removed.has(p.eaPlayerId));
            state.selectedPlayers = [];
            state.isSelecting = false;
        })
            .addCase(fetchMyClubPlayers.rejected, (state) => {
            state.status = "rejected";
        });
    },
});
export const { updateShowFilters, updateFilters, updateManuallyAddPlayerProperties, updateIsSelecting, selectPlayer, deselectPlayer, } = myPlayersSlice.actions;
export const myPlayersSelector = (state) => state.myPlayersReducer;
// Helper function to convert filters object to Set for API calls
export const getActiveFilters = (filters) => {
    return new Set(Object.entries(filters)
        .filter(([_, isActive]) => isActive)
        .map(([filter]) => filter));
};
export const myPlayersReducer = myPlayersSlice.reducer;
export default myPlayersSlice.reducer;
//# sourceMappingURL=myPlayersSlice.js.map